/**
 * Search Filters Component
 *
 * Provides filter options for search results.
 */

import { useState, useCallback } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ChevronDown, ChevronUp, Filter } from 'lucide-react'
import type { SearchField, SearchSortField, SearchSortOrder } from '@/services/steemworld'

interface SearchFiltersProps {
  filters: {
    author?: string
    tags?: string
    searchIn?: SearchField
    sortBy?: SearchSortField
    order?: SearchSortOrder
  }
  onFilterChange: (filters: {
    author?: string
    tags?: string
    searchIn?: SearchField
    sortBy?: SearchSortField
    order?: SearchSortOrder
  }) => void
}

export function SearchFilters({ filters, onFilterChange }: SearchFiltersProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [localFilters, setLocalFilters] = useState(filters)

  const handleApply = useCallback(() => {
    onFilterChange(localFilters)
  }, [localFilters, onFilterChange])

  const handleReset = useCallback(() => {
    const resetFilters = {
      author: undefined,
      tags: undefined,
      searchIn: 'title,body' as SearchField,
      sortBy: 'created' as SearchSortField,
      order: 'desc' as SearchSortOrder,
    }
    setLocalFilters(resetFilters)
    onFilterChange(resetFilters)
  }, [onFilterChange])

  const hasActiveFilters =
    filters.author ||
    filters.tags ||
    filters.searchIn !== 'title,body' ||
    filters.sortBy !== 'created' ||
    filters.order !== 'desc'

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="flex items-center justify-between">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2">
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                Active
              </span>
            )}
            {isOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>

        {/* Quick sort options */}
        <div className="flex items-center gap-2">
          <Select
            value={localFilters.sortBy || 'created'}
            onValueChange={(value: SearchSortField) => {
              const newFilters = { ...localFilters, sortBy: value }
              setLocalFilters(newFilters)
              onFilterChange(newFilters)
            }}
          >
            <SelectTrigger className="w-[130px] h-8">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created">Newest</SelectItem>
              <SelectItem value="payout">Payout</SelectItem>
              <SelectItem value="votes">Votes</SelectItem>
              <SelectItem value="comments">Comments</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={localFilters.order || 'desc'}
            onValueChange={(value: SearchSortOrder) => {
              const newFilters = { ...localFilters, order: value }
              setLocalFilters(newFilters)
              onFilterChange(newFilters)
            }}
          >
            <SelectTrigger className="w-[100px] h-8">
              <SelectValue placeholder="Order" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Desc</SelectItem>
              <SelectItem value="asc">Asc</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <CollapsibleContent className="mt-4">
        <div className="rounded-lg border bg-card p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Author filter */}
            <div className="space-y-2">
              <Label htmlFor="author">Author</Label>
              <Input
                id="author"
                placeholder="e.g., steemit"
                value={localFilters.author || ''}
                onChange={(e) =>
                  setLocalFilters({
                    ...localFilters,
                    author: e.target.value || undefined,
                  })
                }
              />
            </div>

            {/* Tags filter */}
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                placeholder="e.g., steem,crypto"
                value={localFilters.tags || ''}
                onChange={(e) =>
                  setLocalFilters({
                    ...localFilters,
                    tags: e.target.value || undefined,
                  })
                }
              />
            </div>
          </div>

          {/* Search in field */}
          <div className="space-y-2">
            <Label>Search in</Label>
            <Select
              value={localFilters.searchIn || 'title,body'}
              onValueChange={(value: SearchField) =>
                setLocalFilters({ ...localFilters, searchIn: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select field" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="title,body">Title & Body</SelectItem>
                <SelectItem value="title">Title only</SelectItem>
                <SelectItem value="body">Body only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={handleReset}>
              Reset
            </Button>
            <Button size="sm" onClick={handleApply}>
              Apply Filters
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
